import { throttle, times, isFunction } from 'lodash';
/**
 * @ngdoc directive
 * @name adjust.directive:scroll
 * @scope
 * @restrict A
 *
 * @description
 * Scroll container directive
 *
 * @param {string} instance scroll instance identifier
 * @param {Function} update update callback
 * @param {boolean} loading loading state
 *
 */
function scroll() {
  'ngInject';
  return {
    restrict: 'A',
    scope: {},
    controller: 'ScrollController',
    controllerAs: 'vm',
    bindToController: {
      instance: '@scroll',
      update: '&onScroll',
      loading: '=scrollLoading',
    },
  };
}

/**
 * @ngdoc controller
 * @name adjust.controller:ScrollController
 *
 * @description
 * All the functionality of the {@link adjust.directive:scroll scroll} directive
 *
 */
function ScrollController($element, $timeout, $scope, Scroll, Helper, $q, $window) {
  'ngInject';
  $element.addClass('scroll-container');

  var vm = this;

  var _lazyScroll = throttle(_handleScroll, 500);
  var _lazyResize = throttle(_handleSet, 300);
  // eslint-disable-next-line prefer-destructuring
  var _container = $element[0];
  var _side;
  var _table;
  var _tid;
  var _tryAuto;
  var _callbacks = {};

  vm.on = on;
  vm.getInstance = getInstance;
  vm.getElement = getElement;
  vm.setScrollTable = setScrollTable;
  vm.scrollLeft = scrollLeft;
  vm.setNoSelect = setNoSelect;
  vm.getScrollTable = getScrollTable;
  vm.setSide = setSide;
  vm.getSide = getSide;

  $scope.$on('scroll:set:' + vm.instance, _handleSet);
  $scope.$on('scroll:set:remote:' + vm.instance, _handleSetRemote);
  $scope.$on('scroll:updating:' + vm.instance, _handleUpdating);
  $scope.$on('scroll:resize:' + vm.instance, _prepare);

  $scope.$watch('vm.loading', _handleChange);

  $element.bind('scroll', _lazyScroll);
  angular.element($window).on('resize', _lazyResize);
  $element.on('$destroy', _handleDestroy);

  /**
   * @ngdoc method
   * @name _handleChange
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Handle loading state change, notify children about it
   *
   * @param {boolean} loading loading state
   * @private
   */
  function _handleChange(loading) {
    if (loading) {
      return;
    }

    _trigger('loading:' + vm.instance, [loading]);
  }

  /**
   * @ngdoc method
   * @name _handleScroll
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Check if scrolled to the bottom and update the list accordingly
   *
   * @private
   */
  function _handleScroll() {
    if (_tryAuto) {
      return;
    }

    var currentBottom = _container.scrollTop + _container.offsetHeight;

    if (currentBottom >= _container.scrollHeight - 200) {
      if (!Scroll.isLoading(vm.instance) && Scroll.hasNext(vm.instance)) {
        vm.update();
      }
    }
  }

  /**
   * @ngdoc method
   * @name _initAutoScroll
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Determine how many times to trigger auto-scroll if list is too short
   *
   * @private
   */
  function _initAutoScroll() {
    if (!_side || !_container) {
      _tryAuto = false;
      return;
    }

    var scrollTimes = Math.floor(_container.offsetHeight / _side.offsetHeight) + 1;
    var promise;

    times(scrollTimes, function() {
      promise = promise ? promise.then(_autoScroll) : _autoScroll();
    });

    _tryAuto = false;
  }

  /**
   * @ngdoc method
   * @name _autoScroll
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Trigger auto scroll (populate) if list is not already loaded entirely
   *
   * @private
   */
  function _autoScroll() {
    if (Scroll.isLoaded(vm.instance)) {
      return $q.when();
    }

    if (Scroll.hasNext(vm.instance)) {
      return vm.update();
    }
  }

  /**
   * @ngdoc method
   * @name _setCls
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Toggle class for the scroll element
   *
   * @param {string} cls class to (un)set
   * @param {boolean} add to set or unset
   * @private
   */
  function _setCls(cls, add) {
    if (add) {
      $element.addClass(cls);
    } else {
      $element.removeClass(cls);
    }
  }

  /**
   * @ngdoc method
   * @name on
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Register callback for particular action
   * Exposed for children
   *
   * @param {string} action action to fire
   * @param {Function} callback callback for the action
   */
  function on(action, callback) {
    _callbacks[action] = callback;
  }

  /**
   * @ngdoc method
   * @name _trigger
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Trigger callback for particular action
   *
   * @param {string} action action to fire
   * @param {Array} params params to pass
   * @private
   */
  function _trigger(action, params) {
    if (isFunction(_callbacks[action])) {
      _callbacks[action].apply(null, params);
    }
  }

  /**
   * @ngdoc method
   * @name getInstance
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Instance of the directive
   *
   * @returns {string} current instance
   */
  function getInstance() {
    return vm.instance;
  }

  /**
   * @ngdoc method
   * @name getElement
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Get directory's dom element
   *
   * @returns {Element} scroll DOM element
   */
  function getElement() {
    return $element[0];
  }

  /**
   * @ngdoc method
   * @name scrollLeft
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Notify children about horizontal scrolling
   *
   * @param {number} left scroll left position
   */
  function scrollLeft(left) {
    _trigger('scroll:left:' + vm.instance, [left]);
  }

  /**
   * @ngdoc method
   * @name setNoSelect
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Toggle `no-select` class to the scroll directive, in order for the table's
   * text (not) to be selectable while scrolling horizontally
   *
   * @param {boolean} value set or not
   */
  function setNoSelect(value) {
    _setCls('no-select', value);
  }

  /**
   * @ngdoc method
   * @name setScrollTable
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Cache scrolling table DOM element
   *
   * @param {Element} table DOM element for the table directive
   */
  function setScrollTable(table) {
    _table = table;
  }

  /**
   * @ngdoc method
   * @name getScrollTable
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Return scrolling table DOM element
   *
   * @returns {Element} table DOM element for the table directive
   */
  function getScrollTable() {
    return _table;
  }

  /**
   * @ngdoc method
   * @name setSide
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Set side list DOM element
   *
   * @param {Element} side list DOM element
   */
  function setSide(side) {
    _side = side;

    _initAutoScroll();
  }

  /**
   * @ngdoc method
   * @name getSide
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Return side list DOM element
   *
   * @return {Element} side list DOM element
   */
  function getSide() {
    return _side;
  }

  /**
   * @ngdoc method
   * @name _prepare
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Prepare scrolling area
   *
   * @private
   */
  function _prepare() {
    _tryAuto = true;
    _container.scrollTop = 0;

    Helper.destroyTid(_tid);

    // wait for scope to digest if search is performed
    _tid = $timeout(_initAutoScroll, 200);
  }

  /**
   * @ngdoc method
   * @name _handleSet
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Handle scrollable list set
   *
   * @param {Object} e event object
   * @private
   */
  function _handleSet(e, list, order) {
    Scroll.setList(vm.instance, list, order);

    _prepare();
  }

  /**
   * @ngdoc method
   * @name _handleSetRemote
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Handle scrollable remote list set
   *
   * @param {Object} e event object
   * @param {boolean} value set or unset remote
   * @private
   */
  function _handleSetRemote(e, value) {
    Scroll.setRemote(vm.instance, value);

    if (value) {
      _prepare();
    }
  }

  /**
   * @ngdoc method
   * @name _handleUpdating
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Handle updating state of the list, display loading bar while list is updating
   *
   * @param {Object} e event object
   * @param {boolean} loading loading state
   * @private
   */
  function _handleUpdating(e, loading) {
    Scroll.setUpdating(vm.instance, loading);

    _setCls('loading-more', loading);
  }

  /**
   * @ngdoc method
   * @name _handleDestroy
   * @methodOf adjust.controller:ScrollController
   *
   * @description
   * Unbind scroll event and destroy Scroll service
   *
   * @private
   */
  function _handleDestroy() {
    Helper.destroyTid(_tid);

    $element.unbind('scroll', _lazyScroll);
    angular.element($window).off('resize', _lazyResize);
  }
}

export { ScrollController, scroll };
